import type { AssessmentResultRow, BulkActionRequest } from '@nest-recruit/shared';
import type { RejectChoice } from '../sorting/hooks/use-sorting-session';

// ASM-07's decisions that don't need React (Redesign Group 4, `Job-assessment.dc.html`).

/**
 * R8: a result never decides for anyone, so Advance is never blocked. When some of the people
 * chosen haven't passed, the toast says so, so the person sees what they did.
 */
export function advanceWarning(rows: readonly AssessmentResultRow[]): string | null {
  const notPassed = rows.filter((row) => row.latest?.didPass !== true).length;
  if (notPassed === 0) return null;
  return notPassed === 1 ? "1 hasn't passed" : `${notPassed} haven't passed`;
}

/** ADR 0046, as Review and Shortlist: offered when the viewer may add and someone chosen has a CV. */
export function offersCvBank(rows: readonly AssessmentResultRow[], mayAddToCvBank: boolean): boolean {
  return mayAddToCvBank && rows.some((row) => row.hasCv);
}

/** The pipeline's own bulk reject on the ids this screen picked, with the CV Bank tick. */
export function rejectRequestOf(rows: readonly AssessmentResultRow[], choice: RejectChoice): BulkActionRequest {
  return {
    action: 'reject',
    selection: { kind: 'ids', ids: rows.map((row) => row.applicationId) },
    reasonCode: choice.reasonCode,
    poolIds: [],
    newPoolNames: [],
    addToCvBank: choice.addToCvBank,
  };
}

/** "Copy link", like the row's other actions, only for someone still in Assessment. */
export function canCopyLink(row: AssessmentResultRow): boolean {
  return row.stage === 'assessment' && row.latest?.isLinkOpen === true;
}

/** Record and Correct: Save waits for a score and a total, with the score no more than the total. */
export function isScoreValid(score: number | undefined, outOf: number | undefined): boolean {
  if (score === undefined || outOf === undefined || !Number.isFinite(score) || !Number.isFinite(outOf)) return false;
  return score >= 0 && outOf > 0 && score <= outOf;
}
